import { memo } from "react"

import { Handle, Position } from "@xyflow/react"
import { useTranslation } from "react-i18next"

import type { HostedAgentMapNode as HostedAgentMapNodeModel } from "./hosted-agent-map-utils"
import type { NodeProps } from "@xyflow/react"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

import { HostedAgentLifecycleStatusBadge } from "../hosted-agent-lifecycle-status-badge"
import { HOSTED_AGENT_MAP_NODE_WIDTH, truncateHostedAgentMission } from "./hosted-agent-map-utils"

/**
 * One agent card on the map: avatar (initials/colour), name, title, a
 * shortened mission and the enabled/paused/lifecycle chips. Target handle on
 * the left, source handle on the right — a drag from the right handle of A
 * onto B creates the directed link A→B.
 */
function HostedAgentMapNodeComponent({ data, selected }: NodeProps<HostedAgentMapNodeModel>) {
  const { t } = useTranslation()
  const { agent, lifecycle } = data
  const mission = truncateHostedAgentMission(agent.profile.description)

  return (
    <div
      style={{ width: HOSTED_AGENT_MAP_NODE_WIDTH }}
      className={cn(
        "bg-card text-card-foreground relative flex flex-col gap-2 rounded-lg border p-3 shadow-sm transition-shadow",
        selected ? "ring-primary/35 ring-2" : "hover:shadow-md",
        !agent.enabled && "opacity-70"
      )}
    >
      <Handle
        position={Position.Left}
        type="target"
        aria-label={t("adminDashboard.hostedAgents.map.targetHandle", {
          defaultValue: "Incoming links",
        })}
      />
      <div className="flex min-w-0 items-center gap-3">
        <span
          aria-hidden="true"
          className={`border-foreground flex size-9 shrink-0 items-center justify-center rounded-full border text-xs font-semibold ${agent.profile.avatar.color ? "text-white" : "text-chart-foreground"}`}
          style={{ backgroundColor: agent.profile.avatar.color || "hsl(var(--chart-1))" }}
        >
          {agent.profile.avatar.initials || agent.profile.name.slice(0, 2).toLocaleUpperCase()}
        </span>
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold">{agent.profile.name}</span>
          <span className="text-muted-foreground truncate text-xs">
            {agent.profile.title || <span className="font-mono">{agent.slug}</span>}
          </span>
        </div>
      </div>
      {mission ? (
        <p className="text-muted-foreground line-clamp-2 text-xs leading-snug">{mission}</p>
      ) : null}
      <div className="flex flex-wrap items-center gap-1">
        {agent.enabled ? (
          <Badge variant="success">
            {t("adminDashboard.hostedAgents.statusEnabled", { defaultValue: "Enabled" })}
          </Badge>
        ) : (
          <Badge variant="disabled">
            {t("adminDashboard.hostedAgents.statusDisabled", { defaultValue: "Disabled" })}
          </Badge>
        )}
        {agent.paused ? (
          <Badge variant="warning">
            {t("adminDashboard.hostedAgents.statusPaused", { defaultValue: "Paused" })}
          </Badge>
        ) : null}
        {lifecycle ? (
          <HostedAgentLifecycleStatusBadge action={lifecycle.action} status={lifecycle.status} />
        ) : null}
      </div>
      <Handle
        position={Position.Right}
        type="source"
        aria-label={t("adminDashboard.hostedAgents.map.sourceHandle", {
          defaultValue: "Drag to link this agent to another",
        })}
      />
    </div>
  )
}

export const HostedAgentMapNode = memo(HostedAgentMapNodeComponent)
